import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery } from "../_app/lib/query";
import {
  getWdrStatus,
  getWdrStatusCostCentre,
  getWdrStatusForAsset,
  getWdrStatusForAssetCostCentre,
  setWdrStatus,
  setWdrStatusCostCentre,
  setWdrStatusForAsset,
  setWdrStatusForAssetCostCentre,
} from "./api";
import { WdrStatusChange } from "./types";
import { useFeedbackAlerts } from "../_app/hooks";
import { UserType } from "../user-level/types";
import { useContextProvider } from "../context/providers/ContextProvider";

export function useWdrStatus(page: number, limit: number, queries?: any[], assets?: string[], options = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId, userType } = useContextProvider();

  const callback = userType === UserType.COST_CENTRE ? getWdrStatusCostCentre : getWdrStatus;

  return useQuery<any>({
    queryKey: ["wdr-status", { page, limit, queries, assets }],
    queryFn: () => callback(contextId, page, limit, queries, assets),
    ...options,
    staleTime: 0,

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message);
    },
  });
}

export function useWdrStatusForAsset(assetId: string, options: any = {}) {
  const { contextId, userType } = useContextProvider();

  return useQuery<any>({
    queryKey: ["wdr-status", { assetId }],

    queryFn: () =>
      userType === UserType.COST_CENTRE ? getWdrStatusForAssetCostCentre(contextId, assetId) : getWdrStatusForAsset(assetId),

    ...options,
  });
}

export function useSetWdrStatus(values: any, options: any = {}) {
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();

  const { contextId, userType } = useContextProvider();
  const callback = userType === UserType.COST_CENTRE ? setWdrStatusCostCentre : setWdrStatus;

  return useMutation({
    mutationFn: (_vars: any) => callback(contextId, values),
    ...options,

    onSuccess: (data) => {
      setFeedbackAlertSuccess(data);
      options?.onSuccess?.();
    },

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message);
    },
  });
}

export function useSetWdrStatusForAsset(assetId: string, options: any = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId, userType } = useContextProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (statusChange: WdrStatusChange) =>
      userType === UserType.COST_CENTRE
        ? setWdrStatusForAssetCostCentre(contextId, statusChange)
        : setWdrStatusForAsset(statusChange),

    ...options,

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["wdr-status", { assetId }],
      });
    },
  });
}
